<template>
  <div>
    <v-card-title>
      <v-flex xs-2 class="mx-4">
        <v-select
          :items="categoriesList"
          item-text="name"
          item-value="id"
          v-model="categories"
          label="Category Filter"
        />
      </v-flex>
      <v-flex xs-2 class="mx-4">
        <v-select
          :items="visibilityStatusOfCompany.statuses"
          v-model="visibilityStatus"
          label="Visibility Status"
        />
      </v-flex>
      <v-flex xs-1>
        <v-btn color="primary" @click="reset">Reset</v-btn>
      </v-flex>
      <v-flex xs-1>
        <v-btn :color="hideOrShowEnvelopBtnColor" @click="doHideOrShowEnvelop">
          {{ hideOrShowBtnTextEnvelop }}
        </v-btn>
      </v-flex>
      <v-flex xs-3 offset-xs5>
        <v-text-field
          append-icon="search"
          label="Search"
          single-line
          hide-details
          v-model="searchValue"
        />
      </v-flex>
    </v-card-title>

    <v-data-table
      v-if="loadTable"
      :headers="headers"
      :items="getCompanies"
      class="elevation-1"
      :loading="loading"
      :pagination.sync="pagination"
      :total-items="getTotalApprovedCompanies"
      :disable-initial-sort="true"
    >
      <template slot="items" slot-scope="props">
        <td class="text-xs">
          <v-flex align-center>{{ props.item.metadata.companyName || 'No name' }}</v-flex>
        </td>
        <td class="text">{{ getCategoriesNameById(props.item.metadata.category) }}</td>

        <td class="text-xs-center">{{ props.item.blitzInvitation.status }}</td>

        <td class="text-xs">
          <v-switch
            v-model="props.item.blitzCard.status"
            :label="props.item.blitzCard.status"
            true-value="Enabled"
            false-value="Disabled"
            :disabled="!isWriteAvailable"
            @change="onChangeStatus({companyId: props.item.id , newStatus: props.item.blitzCard.status})"
          />
        </td>

        <td class="text-xs-center">
          <v-tooltip
            v-if="props.item.blitzInvitation.status !== 'NotYet'"
            bottom
          >
            <v-btn
              color="lime"
              :disabled="!isWriteAvailable"
              fab flat
              slot="activator"
              @click="onShowEditForm(props.item)"
            >
              <v-icon>create</v-icon>
            </v-btn>
            <span>Edit</span>
          </v-tooltip>
        </td>

      </template>

      <template slot="no-data" v-if="!getSpinnerVisibilityStatus">
        <v-alert :value="true" color="error" icon="warning">
          Sorry, nothing to display here :(
        </v-alert>
      </template>

      <template slot="no-results">
        <v-alert :value="true" color="error" icon="warning">
          Your search for "{{ search }}" found no results.
        </v-alert>
      </template>
    </v-data-table>

    <v-dialog
      persistent
      v-model="showEditForm"
      fullscreen
      transition="dialog-bottom-transition"
      :overlay="false"
      scrollable
    >
      <v-card tile>
        <v-toolbar card dark color="primary">
          <v-btn icon @click.native="onCloseModal" dark>
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title v-if="editableCompany">
            {{ editableCompany.metadata.companyName || editableCompany.blitzInvitation.company || '' }} Blitz
            Registration
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark flat @click.native="onSaveEditableForm">Save</v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div v-if="showEditForm">
            <form-interpretator
              v-if="getFormData && editableCompany"
              :formType="getFormData.type"
              :formId="getFormData.id"
              :formGroup="getFormData.group"
              :formFields="getFormData.fields"
              :formMetadata="getFormData.metadata"
              :editableItem="editableCompany"
              ref="formInterpretator"
              @submit="onSubmitForm"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import debounce from 'lodash.debounce'
  import { equals, isNil } from 'ramda'
  import FormInterpretator from '@/components/shared/EvFormInterpretator'
  import { categoryOfCompany, visibilityStatusOfCompany, sortWay } from '@/modules/settings/'
  import mixins from '@/utils/mixins'

  export default {
    name: 'ApprovedCompanies',
    mixins: [mixins.isWriteAvailable],
    components: {
      FormInterpretator
    },
    data () {
      return {
        categoryOfCompany,
        visibilityStatusOfCompany,
        sortWay,
        loadTable: false,
        categories: '',
        visibilityStatus: '',
        showEditForm: false,
        editableCompany: null,
        searchValue: '',
        loading: true,
        pagination: {
          page: 1,
          rowsPerPage: 10
        },
        headers: [
          { text: 'Company Name', align: 'left', value: 'company', search: true, sortable: true, width: 400 },
          { text: 'Category', value: 'category', search: true, sortable: true, width: 300 },
          { text: 'Status', value: 'status', search: false, sortable: false, align: 'center', width: 250 },
          { text: 'Disable/Enable', search: false, sortable: false, align: 'center', width: 150 },
          { text: 'Edit', search: false, sortable: false, align: 'center', width: 50 }
        ],
        // DON'T DELETE! need for isWriteAvailable mixin
        scopeForWrite: 'blitz:write'
      }
    },
    computed: {
      ...mapGetters([
        'getCompanies',
        'getTotalApprovedCompaniesPages',
        'getTotalApprovedCompanies',
        'getSpinnerVisibilityStatus',
        'getFormData',
        'categoriesList',
        'getBlitzSettingHideEnvelope'
      ]),
      hideOrShowBtnTextEnvelop () {
        return this.getBlitzSettingHideEnvelope ? 'SHOW ENVELOPE' : 'HIDE ENVELOPE'
      },
      hideOrShowEnvelopBtnColor () {
        return this.getBlitzSettingHideEnvelope ? 'success' : 'error'
      },
    },
    watch: {
      searchValue () {
        this.search()
      },
      pagination: {
        handler (val, oldVal) {
          if (val.totalItems !== oldVal.totalItems) return
          this.search()
        },
        deep: true
      },
      categories (val) {
        if (val !== '') this.search(val, 'category')
      },
      visibilityStatus (val) {
        if (val !== '') this.search(val, 'blitzCard.status')
      }
    },
    methods: {
      ...mapActions([
        'fetchCompaniesList',
        'updateCompanies',
        'getInterpritationFormData',
        'getCategoriesList',
        'hideOrShowEnvelop',
        'fetchGeneralSettings'
      ]),
      async doHideOrShowEnvelop () {
        await this.hideOrShowEnvelop ({ hideEnvelope: !this.getBlitzSettingHideEnvelope })
        return await this.fetchGeneralSettings()
      },
      getCategoriesNameById (id) {
        if (!id) return
        const category = this.categoriesList.find(el => el.id === id)
        return !isNil(category) ? category.name : ''
      },
      search: debounce(async function (searchWord, filterBy) {
        this.loading = true
        const { sortBy, descending, page, rowsPerPage } = this.pagination
        const newPage = page - 1
        const pageCheck = newPage > 0 ? newPage : 0
        const sorted = !!sortBy ? this.sortWay[sortBy] : null
        const sorter = {
          direction: this.sortingDirection(descending),
          sortBy: sorted
        }
        const filter = {
          searchValue: this.searchValue,
          filterBy: this.sortWay['company']
        }
        const equalsStatus = {
          searchWord,
          filterBy: this.sortWay[filterBy]
        }
        const getParams = {
          page: pageCheck,
          itemsOnPage: equals(rowsPerPage, -1) ? this.getTotalApprovedCompanies : rowsPerPage,
          sorter,
          filter,
          equalsStatus
        }
        try {
          await this.fetchCompaniesList(getParams)
          this.loadTable = true
        } catch (e) {
          console.log(e)
        } finally {
          this.loading = false
        }
      }, 250),
      async reset () {
        this.visibilityStatus = ''
        this.categories = ''
        await this.search()
      },
      sortingDirection (dir) {
        return dir ? 'Desc' : 'Asc'
      },
      onChangeStatus ({ companyId, newStatus }) {
        this.updateCompanies([
          {
            id: companyId,
            status: newStatus
          }
        ])
      },
      hideEnvelope () {

      },
      onShowEditForm (companyData) {
        this.showEditForm = true
        this.editableCompany = companyData
      },
      onSaveEditableForm () {
        this.$refs.formInterpretator.submitForm()
      },
      onSubmitForm (form) {
        this.updateCompanies(
          [{
            id: this.editableCompany.id,
            body: form
          }]
        )
      },
      onCloseModal () {
        this.showEditForm = false
      }
    },
    async mounted () {
      await this.getInterpritationFormData('blitz_registration')
      await this.search()
      await this.getCategoriesList({'itemsOnPage': 9999, 'page': 0})
      await this.fetchGeneralSettings()
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/pages/Blitz/approved-companies/index.vue